/**
 * What the sim says about a prop that has two states (propModels.ts `SWITCHED`). Pure: it reads the
 * world and the mission and answers; `StaticWorld.syncPropStates` does the swapping.
 *
 * - A breaker or a generator (starts `on`) is OFF once it has been worked (`usedPropIds`) or the
 *   blackout group it switches has gone dark - the same two conditions `sim/objectives/blackout.ts` uses.
 * - The strike beacon (starts `off`: only a marked spot) is UP once the quest item has been delivered
 *   to it, and GONE once the strike it called in has landed on it.
 */

import type { MissionDef, PropDef, World } from '../../sim/types';
import { initialSwitch, type PropSwitch } from './propModels';

/** The state `prop` should be shown in; `gone` = take it away; null = not a switched kind. */
export function wantedSwitch(def: MissionDef, world: World, prop: PropDef): PropSwitch | 'gone' | null {
  const initial = initialSwitch(prop.kind);
  if (initial === null) return null;
  const o = world.objective;
  if (initial === 'on') {
    const dark = def.blackoutGroups?.some((g, i) => g.switchPropId === prop.id && o.blackouts[i] === true) ?? false;
    return dark || o.usedPropIds.includes(prop.id) ? 'off' : 'on';
  }
  if (def.objective.deliverToPropId !== prop.id || o.item !== 'delivered') return 'off';
  const struck = o.timed.some((t) => {
    const rule = def.objective.rules[t.rule];
    return t.fired && rule?.type === 'timedEvent' && rule.effect === 'strikeLands';
  });
  return struck ? 'gone' : 'on';
}
